import axios from 'axios'
export const BASE_URL = process.env.VUE_APP_PERKI_API_URL || 'http://localhost:3000/api/v1'
export const FILES_URL = process.env.VUE_APP_PERKI_FILES_URL || 'http://localhost:3000/files'

// Debug log
console.log('PERKI API Configuration:', {
  BASE_URL,
  FILES_URL,
})

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
})

// Request interceptor - add token to requests
apiClient.interceptors.request.use(
  config => {
    // Debug log
    // console.log('API Request:', {
    //   method: config.method?.toUpperCase(),
    //   url: config.baseURL + config.url,
    //   data: config.data,
    //   headers: config.headers,
    // })

    let token = localStorage.getItem('perki_token')
    if (window.location.pathname.startsWith('/mobile-cms')) {
      token = localStorage.getItem('mobile_cms_token')
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Request Interceptor Error:', error)
    return Promise.reject(error)
  },
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  response => {
    // Debug log
    // console.log('API Response:', {
    //   status: response.status,
    //   data: response.data,
    // })
    return response
  },
  error => {
    // Debug log
    console.error('API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        method: error.config?.method,
        url: error.config?.url,
        data: error.config?.data,
      },
    })

    if (error.response?.status === 401) {
      // Skip auto-logout untuk endpoint check-password karena 401 adalah response yang valid (password salah)
      const isCheckPasswordEndpoint = error.config?.url?.includes('/check-password')

      if (!isCheckPasswordEndpoint) {
        const token = localStorage.getItem('perki_token')

        // Cek dulu apakah ada token (berarti kemungkinan expired/invalid)
        if (token) {
          localStorage.removeItem('perki_token')
          localStorage.removeItem('perki_user')
        }

        // Simpan path sekarang untuk redirect setelah login
        const currentPath = window.location.pathname + window.location.search + window.location.hash

        // Hindari redirect loop kalau sudah di halaman login
        // if (!window.location.pathname.startsWith('/masuk')) {
        //   window.location.href = `/masuk?redirect=${encodeURIComponent(currentPath)}`
        // } else {
        //   window.location.href = '/masuk'
        // }
      }
    }

    return Promise.reject(error)
  },
)

// Helper to get full file URL
export const getFileUrl = path => {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return `${FILES_URL}${path}`
}

// Auth APIs
export const authAPI = {
  login: async data => await apiClient.post('/auth/login', data),
  register: async data => await apiClient.post('/auth/register', data),
  forgotPassword: async data => await apiClient.post('/auth/forgot-password', data),
  confirmForgotPassword: async (token, data) =>
    await apiClient.post(`/auth/confirm-forgot-password?token=${token}`, data),
  checkNIK: async nik => await apiClient.post('/auth/check-nik', { nik }),
  checkEmail: async email => await apiClient.post('/auth/check-email', { email }),
}

// Content APIs
export const contentAPI = {
  getAll: async params => await apiClient.get('/contents', { params }),
  getBySlug: slug => apiClient.get(`/contents/slug/${slug}`),
  getById: async id => await apiClient.get(`/contents/${id}`),
}

// Member APIs (Protected)
export const memberAPI = {
  getAll: async params => await apiClient.get('/members/', { params }),
  getLinked: async params => await apiClient.get('/members/linked', { params }),
  getById: async id => await apiClient.get(`/members/${id}`),
  getByPerkiId: async perkiId => await apiClient.get(`/members/perki/${perkiId}`),
  create: async data => await apiClient.post('/members', data),
  update: async (id, data) => await apiClient.put(`/members/${id}`, data),
  delete: async id => await apiClient.delete(`/members/${id}`),
  toggleActive: async id => await apiClient.patch(`/members/${id}/toggle-active`),
  uploadAvatar: async (id, formData) =>
    await apiClient.post(`/members/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

// User APIs (Protected - Admin & Super Admin)
export const userAPI = {
  getAll: params => apiClient.get('/users/', { params }),
  getById: id => apiClient.get(`/users/${id}`),
  create: data => apiClient.post('/users/', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  delete: id => apiClient.delete(`/users/${id}`),
  getProfile: () => apiClient.get('/users/profile'),
  updateProfile: data => apiClient.put('/users/profile', data),
  // Get users in current user's group (for dropdowns like Perwakilan Dinas, Verifikator)
  getUsersInMyGroup: params => apiClient.get('/users/my-group', { params }),
}

// Group APIs (Protected - Super Admin Only)
export const groupAPI = {
  getAll: params => apiClient.get('/groups', { params }),
  getById: id => apiClient.get(`/groups/${id}`),
  create: data => apiClient.post('/groups', data),
  update: (id, data) => apiClient.put(`/groups/${id}`, data),
  delete: id => apiClient.delete(`/groups/${id}`),
}

// Surat Masuk APIs (Protected)
export const suratMasukAPI = {
  getAll: params => apiClient.get(`/surat-masuk/group`, { params }),
  getById: id => apiClient.get(`/surat-masuk/${id}`),
  create: data => apiClient.post('/surat-masuk', data),
  update: (id, data) => apiClient.put(`/surat-masuk/${id}`, data),
  delete: id => apiClient.delete(`/surat-masuk/${id}`),
  // Comments API - get all comments grouped by disposisi
  getComments: id => apiClient.get(`/surat-masuk/${id}/comments`),
}

// Disposisi APIs (Protected)
export const disposisiAPI = {
  getMyTasks: params => apiClient.get('/disposisi/my-tasks', { params }),
  getById: id => apiClient.get(`/disposisi/${id}`),
  getPekerjaHarianById: id => apiClient.get(`/disposisi/pekerja-harian/${id}`),
  getPengurusById: id => apiClient.get(`/disposisi/pengurus/${id}`),
  // Status update API
  updateStatus: (type, id) => apiClient.put(`/disposisi/${type}/${id}/status`),
  // Comment APIs
  getComments: (type, id) => apiClient.get(`/disposisi/${type}/${id}/comments`),
  createComment: (type, id, data) => apiClient.post(`/disposisi/${type}/${id}/comments`, data),
  updateComment: (type, id, commentId, data) =>
    apiClient.put(`/disposisi/${type}/${id}/comments/${commentId}`, data),
  deleteComment: (type, id, commentId) =>
    apiClient.delete(`/disposisi/${type}/${id}/comments/${commentId}`),
}

export const uploadFilesAPI = {
  uploadFiles: formData =>
    apiClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

// Document Extraction API (backend calls OpenAI for auto-detection)
export const documentExtractionAPI = {
  // Extract document info from uploaded file (for Surat Masuk)
  extractFromFile: formData =>
    apiClient.post('/extract-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Extract document info from file URL
  extractFromUrl: fileUrl => apiClient.post('/extract-document-url', { fileUrl }),
  // Extract document info for Surat Keluar archiving (jenis_surat, no_surat, tanggal_surat, penandatangan)
  extractForSuratKeluar: formData =>
    apiClient.post('/extract-document-keluar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

export const signatureAPI = {
  create: formData =>
    apiClient.post('/signatures', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadBase64: data => apiClient.post('/signatures/base64', data),
  getMySignature: () => apiClient.get('/signatures/me'),
  delete: () => apiClient.delete('/signatures'),
}

// surat Settings APIs (Stempel & Kop Surat)
export const suratSettingsAPI = {
  get: () => apiClient.get('/surat-settings'),
  uploadStempel: data => apiClient.post('/surat-settings/stempel', data),
  uploadKopSurat: data => apiClient.post('/surat-settings/kop-surat', data),
  deleteStempel: () => apiClient.delete('/surat-settings/stempel'),
  deleteKopSurat: () => apiClient.delete('/surat-settings/kop-surat'),
}

// Surat Keluar APIs (Protected)
export const suratKeluarAPI = {
  getAll: params => apiClient.get('/surat-keluar', { params }),
  getById: id => apiClient.get(`/surat-keluar/${id}`),
  create: data => {
    // Check if data has files to upload (metodePembuatan = Upload File)
    if (data instanceof FormData) {
      return apiClient.post('/surat-keluar', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return apiClient.post('/surat-keluar', data)
  },
  update: (id, data) => {
    // Check if data has files to upload
    if (data instanceof FormData) {
      return apiClient.put(`/surat-keluar/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return apiClient.put(`/surat-keluar/${id}`, data)
  },
  delete: id => apiClient.delete(`/surat-keluar/${id}`),
  submit: (id, data) => apiClient.post(`/surat-keluar/${id}/submit`, data),
  cancel: (id, data) => apiClient.post(`/surat-keluar/${id}/cancel`, data),
  markSent: id => apiClient.post(`/surat-keluar/${id}/mark-sent`),
  generatePDF: id => apiClient.get(`/surat-keluar/${id}/generate-pdf`),
  getLatestNomorSurat: (jenisSurat = 'Surat Tugas') =>
    apiClient.get('/surat-keluar/latest-nomor', { params: { jenisSurat } }),
  checkNomorSurat: (nomorSurat, excludeId = null) =>
    apiClient.get('/surat-keluar/check-nomor', { params: { nomor: nomorSurat, excludeId } }),
  getSuratMasukOptions: () => apiClient.get('/surat-keluar/surat-masuk-options'),
  archiveHardCopy: (id, formData) =>
    apiClient.post(`/surat-keluar/${id}/archive-hardcopy`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Create archived surat keluar (Arsipkan Surat Keluar flow)
  createArchived: formData =>
    apiClient.post('/surat-keluar/archive', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Preview PDF for specific perwakilan dinas (used in Create flow)
  previewPerwakilanDinasPDF: data =>
    apiClient.post('/surat-keluar/preview-perwakilan', data, {
      responseType: 'blob', // Get PDF as blob
    }),
  // Preview PDF for Surat Lainnya (used in Create flow)
  previewLainnyaPDF: data =>
    apiClient.post('/surat-keluar/preview-lainnya', data, {
      responseType: 'blob', // Get PDF as blob
    }),
  // Upload bukti pengiriman for non-pengurus
  uploadBuktiPengiriman: (suratId, perwakilanId, formData) =>
    apiClient.post(
      `/surat-keluar/${suratId}/perwakilan-dinas/${perwakilanId}/bukti-pengiriman`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    ),
  // Delete bukti pengiriman for non-pengurus
  deleteBuktiPengiriman: (suratId, perwakilanId) =>
    apiClient.delete(`/surat-keluar/${suratId}/perwakilan-dinas/${perwakilanId}/bukti-pengiriman`),
  // Upload bukti pengiriman for surat-level (non-Surat Tugas)
  uploadBuktiPengirimanSurat: (suratId, formData) =>
    apiClient.post(`/surat-keluar/${suratId}/bukti-pengiriman`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Delete bukti pengiriman for surat-level (non-Surat Tugas)
  deleteBuktiPengirimanSurat: (suratId, filename) =>
    apiClient.delete(`/surat-keluar/${suratId}/bukti-pengiriman`, {
      params: { filename },
    }),
}

// Verifikasi APIs (Protected)
export const verifikasiAPI = {
  getSuratForVerifikasi: params => apiClient.get('/verifikasi', { params }),
  getById: id => apiClient.get(`/verifikasi/${id}`),
  approve: id => apiClient.post(`/verifikasi/${id}/approve`),
  revise: (id, data) => apiClient.post(`/verifikasi/${id}/revise`, data),
}

// Penandatanganan APIs (Protected)
export const penandatangananAPI = {
  getSuratForPenandatanganan: params => apiClient.get('/penandatanganan', { params }),
  getById: id => apiClient.get(`/penandatanganan/${id}`),
  sign: (id, data) => apiClient.post(`/penandatanganan/${id}/sign`, data),
  revise: (id, data) => apiClient.post(`/penandatanganan/${id}/revise`, data),
}

// Notification APIs (Protected)
export const notificationAPI = {
  getAll: params => apiClient.get('/notifications', { params }),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  markAsRead: id => apiClient.post(`/notifications/${id}/read`),
  markAllAsRead: () => apiClient.post('/notifications/read-all'),
}

// Notulensi APIs (Protected)
export const notulensiAPI = {
  getAll: params => apiClient.get('/notulensi', { params }),
  getById: id => apiClient.get(`/notulensi/${id}`),
  create: formData =>
    apiClient.post('/notulensi', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, formData) =>
    apiClient.put(`/notulensi/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: id => apiClient.delete(`/notulensi/${id}`),
}

export default apiClient
